import { API_USERNAME, API_PASSWORD } from './config.js.dist';
import {
    getInitialProductsFromCollection,
    getPaginatedProductsFromCollection,
    getFilteredProductsFromCollection,
    getFilteredProductsFromCollectionPagination,
    getInitialProductVariants,
    getPaginatedProductVariants,
} from './queries';
import getProductById from './queries/getProductById';
import searchProductsQuery from './queries/searchProducts';
import searchProductsByTagQuery from './queries/searchProductsByTag';
import getHeadBuilderConfigQuery from './queries/getHeadBuilderConfig';
import { getCountryCode } from './utils/currency';
import { IMAGE_PROPERTY_KEY_SET } from '../shared/uploadFieldConfig';
import { isCreateYourOwnHeadProductId } from './constants';

/**
 * API
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @author Matt Haynes <Matthew.haynes@joipolloi.com>
 * @package Minifigs
 */

/* eslint-disable */

class API {
    // Add this method to the API class in api.js

    /**
     * Get head builder configuration from metaobjects
     *
     * @param {string} productHandle - Current product handle
     * @return Promise
     */
    async getHeadBuilderConfig(productHandle) {
        const variables = { handle: productHandle };

        try {
            const response = await fetch('/api/2025-04/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': 'e14901ef020f42eff2b10fbed13e6e18',
                },
                body: JSON.stringify({ query: getHeadBuilderConfigQuery, variables }),
            });

            const result = await response.json();

            if (result.errors) {
                console.error('[Head Builder Config] GraphQL errors:', result.errors);
                return null;
            }

            return result?.data?.product?.headConfig?.reference || null;
        } catch (error) {
            console.error('[Head Builder Config] Fetch error:', error);
            return null;
        }
    }

    async searchProducts(searchTerm, first = 50, after = null) {
        const query = searchProductsQuery;
        const country = getCountryCode();
        const variables = {
            query: searchTerm,
            first,
            ...(after && { after }),
            ...(country && { country }),
        };
        const response = await fetch('/api/2025-04/graphql.json', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': 'e14901ef020f42eff2b10fbed13e6e18',
            },
            body: JSON.stringify({ query, variables }),
        });
        const result = await response.json();

        // Filter out products without featured images and transform to expected structure
        const filteredEdges =
            result?.data?.products?.edges?.filter((edge) => edge.node.featuredImage && edge.node.featuredImage.url) ||
            [];

        const products = filteredEdges.map((edge) => ({
            node: edge.node,
            cursor: edge.cursor,
            boldProductOptions: ['0', {}], // Default bold options structure
        }));

        return {
            products,
            pageInfo: result?.data?.products?.pageInfo || {
                hasNextPage: false,
                hasPreviousPage: false,
                startCursor: null,
                endCursor: null,
            },
        };
    }

    async searchProductsByTag(tag, first = 50, after = null) {
        // The Storefront API query syntax supports tags via 'tag:"value"'
        const country = getCountryCode();
        const variables = {
            query: `tag:\"${tag}\"`,
            first,
            ...(after && { after }),
            ...(country && { country }),
        };
        const response = await fetch('/api/2025-04/graphql.json', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': 'e14901ef020f42eff2b10fbed13e6e18',
            },
            body: JSON.stringify({ query: searchProductsByTagQuery, variables }),
        });
        const result = await response.json();

        const filteredEdges =
            result?.data?.products?.edges?.filter((edge) => edge.node.featuredImage && edge.node.featuredImage.url) ||
            [];

        const products = filteredEdges.map((edge) => ({
            node: edge.node,
            cursor: edge.cursor,
            boldProductOptions: ['0', {}],
        }));

        return {
            products,
            pageInfo: result?.data?.products?.pageInfo || {
                hasNextPage: false,
                hasPreviousPage: false,
                startCursor: null,
                endCursor: null,
            },
        };
    }

    /**
     * Multi-tag search (AND/OR logic) with optional collection handle.
     * mode: 'AND' (default) requires all tags; 'OR' matches any.
     */
    async searchProductsByTags(tags = [], { mode = 'AND', collectionHandle = null, first = 50, after = null } = {}) {
        if ((!tags || tags.length === 0) && !collectionHandle) {
            return { products: [], pageInfo: { hasNextPage: false, hasPreviousPage: false } };
        }

        const safe = (v) => String(v).replace(/"/g, '\\"');
        const segments = [];
        if (collectionHandle) segments.push(`collection:${safe(collectionHandle)}`);
        for (const t of tags) segments.push(`tag:"${safe(t)}"`);
        const joiner = mode === 'OR' ? ' OR ' : ' AND ';
        const queryStr = segments.join(joiner);

        const country = getCountryCode();
        const variables = { query: queryStr, first, ...(after && { after }), ...(country && { country }) };

        const response = await fetch('/api/2025-04/graphql.json', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': 'e14901ef020f42eff2b10fbed13e6e18',
            },
            body: JSON.stringify({ query: searchProductsByTagQuery, variables }), // reuse same shape
        });
        const result = await response.json();

        const filteredEdges =
            result?.data?.products?.edges?.filter((edge) => edge.node.featuredImage && edge.node.featuredImage.url) ||
            [];

        const products = filteredEdges.map((edge) => ({
            node: edge.node,
            cursor: edge.cursor,
            boldProductOptions: ['0', {}],
        }));

        return {
            products,
            pageInfo: result?.data?.products?.pageInfo || {
                hasNextPage: false,
                hasPreviousPage: false,
                startCursor: null,
                endCursor: null,
            },
        };
    }

    async getConfig() {
        const resp = await fetch('https://minifigs-builder-filter-config.s3.eu-west-2.amazonaws.com/production.json', {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 5000,
        });
        const config = await resp.json();
        return config;
    }

    /**
     * Get the top level categories for a section
     * * * * THIS IS NO LONGER IN USE AS WE DON'T GO INTO SUB CATEGORIES THIS WAY ANY LONGER.
     *
     * @param {string} sectionId
     * @return Promise
     */
    getCategories(sectionId) {
        // These products are all pseudo products, named like: A_MINIFIG_BUILDER_sectionId
        // Can be viewed here: https://minifigs.me/admin/products?query=A_MINI

        // If you add a collection handle to the description of this these products
        // then that collection and all of its products will be added to the relevant place

        const productDescriptions = [
            {
                url: '/products/a_minifig_builder_hatsAndHair.js',
                type: 'hatsAndHair',
            },
            {
                url: '/products/a_minifig_builder_heads.js',
                type: 'heads',
            },
            {
                url: '/products/a_minifig_builder_bodies.js',
                type: 'bodies',
            },
            {
                url: '/products/a_minifig_builder_legs.js',
                type: 'legs',
            },
            {
                url: '/products/a_minifig_builder_display.js',
                type: 'display',
            },
            {
                url: '/products/a_minifig_builder_accessories.js',
                type: 'accessory',
            },
        ];

        const getData = () => {
            return Promise.all(
                productDescriptions.map((product) => {
                    return fetch(product.url)
                        .then((response) => response.json())
                        .then((data) => {
                            return Array.from(data.description.split(','));
                        })
                        .then((data) => {
                            if (product.type === 'accessory') {
                                categoryMap.accessories = data;
                            } else {
                                categoryMap[product.type] = data;
                            }
                        })
                        .catch((err) => {
                            console.error('Fetch Error :-S', err);
                        });
                })
            );
        };

        const categoryMap = {
            hatsAndHair: [],
            heads: [],
            bodies: [],
            legs: [],
            display: [],
            accessories: [],
        };

        if (!sectionId) {
            return Promise.resolve([]);
        }

        if (API.hasCache && window.sessionStorage.getItem(`categories-${sectionId}`)) {
            return Promise.resolve(window.sessionStorage.getItem(`categories-${sectionId}`));
        }

        return getData().then(() => {
            return Promise.all(
                (categoryMap[sectionId] || []).map((collection) => {
                    return fetch(`/collections/${collection}.json`, {
                        method: 'GET',
                        credentials: 'same-origin',
                        redirect: 'follow',
                        agent: null,
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Basic ${btoa(`${API_USERNAME}:${API_PASSWORD}`)}`,
                        },
                        timeout: 5000,
                    })
                        .then((response) => response.json())
                        .then((data) => {
                            for (const key in data) {
                                if (data.hasOwnProperty(key)) {
                                    return {
                                        id: data.collection.id,
                                        title: data.collection.title,
                                        image: data.collection.image.src,
                                        handle: data.collection.handle,
                                    };
                                    // just to show they can be sorted if wanted later...
                                    // .sort((a, b) => a.id - b.id);
                                }
                            }
                        })
                        .catch(function (err) {
                            console.error('Fetch Error :-S', err);
                        });
                })
            );
        });
    }

    /**
     * Get the available products for a category
     *
     * @param {number} productId
     * @return Promise
     */
    getProduct(productId) {
        const fetchMyProducts = (query) => {
            return fetch('/api/2025-04/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/graphql',
                    'Access-Control-Origin': '*',
                    'X-Shopify-Storefront-Access-Token': 'e14901ef020f42eff2b10fbed13e6e18',
                },
                body: query,
            })
                .then((resp) => resp.json())
                .then((result) => {
                    const product = result?.data?.product;
                    return {
                        product: product,
                    };
                })
                .catch(function (err) {
                    console.error('Fetch Error :-S', err);
                    return {
                        product: null,
                        error: err,
                    };
                });
        };

        return fetchMyProducts(getProductById(productId, getCountryCode()));
    }

    /**
     * Get the available products for a category
     *
     * @param {number} categoryId
     * @param {string} categoryHandle
     * @param {object} filters
     * @param {string} afterCursor
     * @return Promise
     */
    getProducts(categoryId, categoryHandle, filters = { namespace: '', key: '', value: '' }, afterCursor, skintone) {
        const country = getCountryCode();
        const cacheKey = `products-${categoryId}-${country || 'default'}`;
        if (API.hasCache && window.sessionStorage.getItem(cacheKey)) {
            return Promise.resolve(window.sessionStorage.getItem(cacheKey));
        }

        let thequery = '';
        const fetchMyProducts = (query) => {
            return fetch('/api/2025-04/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/graphql',
                    'Access-Control-Origin': '*',
                    'X-Shopify-Storefront-Access-Token': 'e14901ef020f42eff2b10fbed13e6e18',
                },
                body: query,
            })
                .then((resp) => resp.json())
                .then((result) => {
                    const products = result?.data?.collection?.products?.edges.filter(
                        (product) =>
                            product.node.id !== 'gid://shopify/Product/3235744972864' &&
                            product.node.id !== 'gid://shopify/Product/3235769024576' &&
                            product.node.id !== 'gid://shopify/Product/3235779969088' &&
                            product.node.id !== 'gid://shopify/Product/3235784294464' &&
                            product.node.id !== 'gid://shopify/Product/3235786784832' &&
                            product.node.id !== 'gid://shopify/Product/3235789307968' &&
                            product.node.id !== 'gid://shopify/Product/3235789307968' &&
                            product.node.id !== 'gid://shopify/Product/3235789307968'
                    );

                    return {
                        products,
                        hasNextPage: result?.data?.collection?.products?.pageInfo.hasNextPage,
                    };
                })
                .catch(function (err) {
                    console.error('Fetch Error :-S', err);
                    return {
                        products: [],
                        hasNextPage: false,
                        error: err,
                    };
                });
        };

        // Heads still use variant-level skintone filtering. Legs now use standard product queries
        if (categoryId === 'heads' && afterCursor !== null && afterCursor !== '') {
            thequery = getPaginatedProductVariants(categoryHandle, skintone, afterCursor, country);
        } else if (categoryId === 'heads') {
            thequery = getInitialProductVariants(categoryHandle, skintone, country);
        } else if (filters && filters.value !== '' && afterCursor !== null && afterCursor !== '') {
            thequery = getFilteredProductsFromCollectionPagination(
                categoryHandle,
                categoryId,
                filters,
                afterCursor,
                country
            );
        } else if (filters && filters.value !== '') {
            thequery = getFilteredProductsFromCollection(categoryHandle, categoryId, filters, country);
        } else if (afterCursor === '' || afterCursor === null) {
            thequery = getInitialProductsFromCollection(categoryHandle, categoryId, country);
        } else {
            thequery = getPaginatedProductsFromCollection(categoryHandle, categoryId, afterCursor, country);
        }

        return fetchMyProducts(thequery);
    }

    /**
     * Get the details of variations on a product
     *
     * @param {number} productId
     * @return Promise
     */
    getVariations(productId, productHandle) {
        if (API.hasCache && window.sessionStorage.getItem(`variations-${productId}`)) {
            return Promise.resolve(window.sessionStorage.getItem(`variations-${productId}`));
        }

        const params = ['limit=250'];

        return fetch(`/products/${productHandle}.json?${params.join('&')}`, {
            method: 'GET',
            credentials: 'same-origin',
            redirect: 'follow',
            agent: null,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Basic ${btoa(`${API_USERNAME}:${API_PASSWORD}`)}`,
            },
            timeout: 5000,
        })
            .then((resp) => resp.json())
            .catch(function (err) {
                console.error('Fetch Error :-S', err);
            });
    }

    /**
     * Retrieving the attribute data based on attribute ID
     * @param attributeId
     * @param productId
     */
    getProductAttribute(attributeId, productId) {
        // example call from existing app...
        // "gid://shopify/Product/3163778646080"
        // https://option.boldapps.net/v2/minifigs-dev.myshopify.com/generate_option/3163778646080?tmp=1661440080900
        return fetch(
            `https://options.shopapps.site/v2/minifigs-dev.myshopify.com/generate_option/${attributeId}?tmp=${Date.now()}`,
            {
                method: 'GET',
                credentials: 'same-origin',
                redirect: 'follow',
                agent: null,
                timeout: 5000,
            }
        )
            .then((response) => response.json())
            .then(function (response) {
                return response;
            })
            .catch(function (err) {
                console.error('Fetch Error :-S', err);
            });
    }

    /**
     * Get the "terms" for an attribute
     *
     * @param {number} attributeId
     * @param {number} productId
     * @return Promise
     */
    getAttributeTerms(attributeId, productId) {
        if (API.hasCache && window.sessionStorage.getItem(`attributeterms-${attributeId}`)) {
            return Promise.resolve(window.sessionStorage.getItem(`attributeterms-${attributeId}`));
        }

        return Promise.resolve();
    }

    /**
     * Add the provided products to the basket
     *
     * @param {object} products
     * @param {string} projectTitle - The name of the project
     * @param {string} skintone - The selected skin tone
     * @param {string} projectKey - Unique project identifier
     * @param {object} headFeatures - Head customization features
     * @return Promise
     */
    async buy(products, projectTitle, skintone, projectKey, headFeatures = null) {
        projectTitle = this.getProjectTitle(projectTitle);
        // let ret = Promise.resolve();
        const requests = [];
        const debugPrefix = '[Builder Buy]';

        function dataURItoBlob(dataURI) {
            const separatorIndex = dataURI.indexOf(',');
            const metadata = dataURI.slice(0, separatorIndex);
            const payload = dataURI.slice(separatorIndex + 1);
            const mimeType = metadata.match(/^data:([^;,]+)/)?.[1] || 'application/octet-stream';
            const binary = metadata.includes(';base64') ? atob(payload) : decodeURIComponent(payload);
            const bytes = new Uint8Array(binary.length);

            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            return new Blob([bytes], { type: mimeType });
        }

        function getUploadFilename(propertyKey, mimeType) {
            const extensionByMimeType = {
                'image/avif': 'avif',
                'image/bmp': 'bmp',
                'image/gif': 'gif',
                'image/heic': 'heic',
                'image/jpeg': 'jpg',
                'image/png': 'png',
                'image/svg+xml': 'svg',
                'image/tiff': 'tiff',
                'image/webp': 'webp',
                'application/pdf': 'pdf',
                'application/postscript': 'eps',
            };
            const extension = extensionByMimeType[mimeType] || 'bin';
            const basename =
                propertyKey
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '') || 'upload';

            return `${basename}.${extension}`;
        }

        const normaliseVariantId = (rawId) => {
            if (!rawId) return rawId;
            if (typeof rawId === 'string') {
                if (rawId.startsWith('gid://')) {
                    const parts = rawId.split('/');
                    const last = parts[parts.length - 1];
                    if (/^\d+$/.test(last)) return last;
                }
                if (/^(shopify|gid):\/\//.test(rawId)) {
                    const digits = rawId.match(/(\d+)/g);
                    if (digits && digits.length) return digits[digits.length - 1];
                }
            }
            return rawId;
        };

        const deriveVariantId = (productObj, pfjv) => {
            return (
                pfjv?.id ||
                pfjv?.variantId ||
                pfjv?.variant_id ||
                productObj.productMatchAttr?.id ||
                productObj.attributes?.id ||
                productObj.attributes?.node?.id ||
                productObj.product?.variants?.nodes?.[0]?.id ||
                productObj.product?.variants?.edges?.[0]?.node?.id ||
                null
            );
        };

        // Add explicit "none" markers for optional sections to first product
        let hasAddedNoneMarkers = false;
        const addNoneMarkersToFirstProduct = (productFormJsonValues) => {
            if (hasAddedNoneMarkers) return;
            hasAddedNoneMarkers = true;

            const optionalSections = ['hatsAndHair', 'display', 'keychain'];
            optionalSections.forEach((sectionKey) => {
                const selectedValue = products[sectionKey];
                if (selectedValue === null || selectedValue === undefined) {
                    productFormJsonValues.properties[`_project_${sectionKey}`] = 'none';
                }
            });

            // Handle accessories separately (it's an array)
            if (!Array.isArray(products.accessories) || products.accessories.length === 0) {
                productFormJsonValues.properties._project_accessories = 'none';
            }
        };

        const handleProductEntry = async (product, keyContext) => {
            if (!product || !product.productFormJsonValues) {
                console.warn(debugPrefix, 'Skip product missing productFormJsonValues', { keyContext, product });
                return;
            }

            const productFormJsonValues = product.productFormJsonValues;

            // Add "none" markers to first product we process
            addNoneMarkersToFirstProduct(productFormJsonValues);

            // Ensure a properties object exists so we can reliably attach projectTitle later.
            if (!productFormJsonValues.properties) {
                productFormJsonValues.properties = {};
            }
            // Guarantee projectTitle & _projectKey present (needed for duplicate detection)
            if (!productFormJsonValues.properties.projectTitle) {
                productFormJsonValues.properties.projectTitle = projectTitle;
            }
            if (projectKey && !productFormJsonValues.properties._projectKey) {
                productFormJsonValues.properties._projectKey = projectKey;
            }

            // Add head feature properties for the custom head product.
            const isHeadProduct = isCreateYourOwnHeadProductId(product.product?.id);
            if (isHeadProduct && headFeatures) {
                // Add facial hair properties
                if (headFeatures.facialHair?.type) {
                    productFormJsonValues.properties._head_facialHairType = headFeatures.facialHair.type;
                }
                if (headFeatures.facialHair?.style) {
                    productFormJsonValues.properties._head_facialHairStyle = headFeatures.facialHair.style;
                }

                // Add other head feature properties (eyebrows, eyes, mouth, glasses, facialFeatures)
                const headFeatureKeys = ['eyebrows', 'eyes', 'mouth', 'glasses', 'facialFeatures'];
                headFeatureKeys.forEach((key) => {
                    if (headFeatures[key]) {
                        // Store the handle/value if available in a future enhancement
                        // For now, we're just documenting this is where it would go
                        // productFormJsonValues.properties[`_head_${key}`] = headFeatures[key];
                    }
                });
            }

            // Resolve variant id BEFORE deciding path (image vs bulk) so both share logic
            let variantId = deriveVariantId(product, productFormJsonValues);
            variantId = normaliseVariantId(variantId);
            if (!variantId) {
                console.error(debugPrefix, 'Cannot resolve variant id prior to processing', {
                    keyContext,
                    productFormJsonKeys: Object.keys(productFormJsonValues),
                    productSnapshot: {
                        productMatchAttr: product.productMatchAttr?.id,
                        attributesId: product.attributes?.id,
                        attributesNodeId: product.attributes?.node?.id,
                        firstNodeVariantId: product.product?.variants?.nodes?.[0]?.id,
                        firstEdgeVariantId: product.product?.variants?.edges?.[0]?.node?.id,
                    },
                });
                return;
            }
            // Persist resolved id & default quantity
            productFormJsonValues.id = variantId;
            if (!productFormJsonValues.quantity) productFormJsonValues.quantity = 1;

            const props = productFormJsonValues.properties;
            const propertyKeys = Object.keys(props || {});
            const dataUriKeysAll = propertyKeys.filter((k) => {
                const v = props?.[k];
                return typeof v === 'string' && v.startsWith('data:');
            });
            const hasImageData = Object.keys(props).some((k) => {
                const v = props[k];
                return IMAGE_PROPERTY_KEY_SET.has(k) && typeof v === 'string' && v.startsWith('data:');
            });
            const matchedImageDataKeys = propertyKeys.filter((k) => {
                const v = props?.[k];
                return IMAGE_PROPERTY_KEY_SET.has(k) && typeof v === 'string' && v.startsWith('data:');
            });

            // If we have any image data fields, submit this item individually via multipart form-data
            if (hasImageData) {
                const formData = new FormData();
                formData.append('quantity', productFormJsonValues.quantity);
                formData.append('id', productFormJsonValues.id);

                for (const key in props) {
                    if (!props.hasOwnProperty(key)) continue;
                    const value = props[key];
                    if (IMAGE_PROPERTY_KEY_SET.has(key) && typeof value === 'string' && value.startsWith('data:')) {
                        try {
                            const blob = dataURItoBlob(value);
                            const filename = getUploadFilename(key, blob.type);
                            const file = new File([blob], filename, { type: blob.type, lastModified: Date.now() });
                            formData.append(`properties[${key}]`, file, filename);
                        } catch (e) {
                            console.warn('Failed to process image field', key, e);
                        }
                    } else {
                        formData.append(`properties[${key}]`, value);
                    }
                }

                const multipartKeys = [];
                for (const [k] of formData.entries()) {
                    multipartKeys.push(k);
                }

                try {
                    const resp = await fetch('/cart/add.js', { body: formData, method: 'POST' });
                    if (!resp.ok) {
                        console.error(debugPrefix, 'Single item add failed', { status: resp.status, keyContext });
                    } else {
                        // console.log(debugPrefix, 'Single item add success', { keyContext });
                    }
                } catch (error) {
                    console.error(debugPrefix, 'Error submitting image formData item', { keyContext, error });
                    console.error('Cart error buy: multipart request error', { keyContext, error });
                }
                return; // Don't also batch add
            }

            const quantity = productFormJsonValues.quantity || 1;
            const parsedQty = parseInt(quantity, 10) || 1;
            const queued = {
                id: variantId,
                quantity: parsedQty,
                properties: productFormJsonValues.properties,
            };
            requests.push(queued);
        };

        for (const key in products) {
            if (key === 'accessories' && Array.isArray(products[key])) {
                for (let i = 0; i < products[key].length; i++) {
                    const accessory = products[key][i];
                    await handleProductEntry(accessory, `accessories[${i}]`);
                }
            } else {
                await handleProductEntry(products[key], key);
            }
        }

        const postData = { items: requests };
        // Function to fetch + POST the data to the Shopify API

        try {
            // Get current cart data
            const cartData = await fetch('/cart.js').then((response) => {
                if (!response.ok) throw new Error('Failed to fetch cart data');
                return response.json();
            });

            const deduped = [];
            for (const requestItem of requests) {
                let isDuplicate = false;
                const matchingItems = cartData.items.filter((item) => {
                    return item.id.toString() === requestItem.id.toString();
                });

                for (const item of matchingItems) {
                    // Prefer projectKey for strict duplicate detection; fallback to projectTitle legacy
                    if (
                        item.properties &&
                        ((projectKey && item.properties._projectKey === projectKey) ||
                            item.properties.projectTitle === projectTitle)
                    ) {
                        isDuplicate = true;
                        break;
                    }
                }

                if (isDuplicate) {
                    console.log(debugPrefix, 'Skipping duplicate item', { id: requestItem.id });
                } else {
                    deduped.push(requestItem);
                }
            }

            if (deduped.length === 0) {
                console.warn(debugPrefix, 'No new (non-duplicate) bulk items to add');
                return { status: 200, message: 'No new items to add' };
            }

            postData.items = deduped;
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'xmlhttprequest',
                },
                body: JSON.stringify(postData),
            });
            let json;
            try {
                json = await response.json();
            } catch (e) {
                console.error(debugPrefix, 'Failed to parse bulk add response JSON', e);
            }
            if (!response.ok) {
                console.error(debugPrefix, 'Bulk add failed', { status: response.status, json });
            } else {
                // console.log(debugPrefix, 'Bulk add success', json);
            }
            return json;
        } catch (error) {
            console.error(debugPrefix, 'Error in cart operations:', error);
            return {
                status: 500,
                error: error.message,
            };
        }
    }

    getProjectTitle(projectTitle) {
        if (projectTitle) {
            return projectTitle;
        }

        const now = new Date();
        let month = '';
        let hour = '';
        let minutes = '';

        switch (now.getMonth()) {
            case 0:
                month = 'January';
                break;
            case 1:
                month = 'February';
                break;
            case 2:
                month = 'March';
                break;
            case 3:
                month = 'April';
                break;
            case 4:
                month = 'May';
                break;
            case 5:
                month = 'June';
                break;
            case 6:
                month = 'July';
                break;
            case 7:
                month = 'August';
                break;
            case 8:
                month = 'September';
                break;
            case 9:
                month = 'October';
                break;
            case 10:
                month = 'November';
                break;
            case 11:
                month = 'December';
                break;
        }

        hour = now.getHours() < 10 ? `0${now.getHours()}` : now.getHours();
        minutes = now.getMinutes() < 10 ? `0${now.getMinutes()}` : now.getMinutes();

        return `Unnamed project ${now.getDate()} ${month} ${now.getFullYear()}, ${hour}:${minutes}`;
    }
}

API.hasCache = typeof window.sessionStorage === 'object';

const api = new API();

// Expose API to window for debugging
// if (typeof window !== 'undefined') {
//     window.debugAPI = api;

//     // Auto-test on the custom head product page
//     window.debugAPI
//         .getHeadBuilderConfig(window.location.pathname.split('/').pop())
//         .then((config) => {
//             if (config) {
//                 // Log each feature group
//                 ['eyebrows', 'eyes', 'mouth', 'facialHair', 'moustacheStyles', 'beardStyles', 'stubbleStyles'].forEach(
//                     (feature) => {
//                         const nodes = config[feature]?.references?.nodes;
//                         if (nodes && nodes.length > 0) {
//                             console.log(
//                                 `  - ${feature}: ${nodes.length} options`,
//                                 nodes.map((n) => n.title?.value || n.handle)
//                             );
//                         }
//                     }
//                 );
//             } else {
//                 console.error('❌ [Head Builder] Failed to load config - check token permissions');
//             }
//         })
//         .catch((err) => {
//             console.error('❌ [Head Builder] Error:', err);
//         });
// }

export default api;
